import { useSettingsRHF } from "../hooks/useSettingsRHF";
import { investableAssetsSchema } from "../types/settingsSchemas";
import type { InvestableAssetsSettingsData } from "../types/settings";

/**
 * Essential fields shown during onboarding and always visible in settings.
 * Core retirement accounts that drive the projection engine.
 */
export const INVESTABLE_ESSENTIAL_FIELDS: (keyof InvestableAssetsSettingsData)[] =
  [
    "rrspBalance",
    "tfsaBalance",
    "nonRegisteredInvestments",
    "cashSavings",
    "emergencyFund",
  ];

/**
 * Advanced fields shown only in full settings via "Show more" toggle.
 * Includes specialized registered accounts, employer plans, and other vehicles.
 */
export const INVESTABLE_ADVANCED_FIELDS: (keyof InvestableAssetsSettingsData)[] =
  [
    "spousalRrspBalance",
    "fhsaBalance",
    "respBalance",
    "liraBalance",
    "rrifBalance",
    "dpspBalance",
    "gicTermDeposits",
    "dbPensionCommutedValue",
    "dcPensionBalance",
    "groupRrspBalance",
    "stockOptionsRsusValue",
  ];

/** All investable assets fields (essential + advanced) */
export const ALL_INVESTABLE_FIELDS: (keyof InvestableAssetsSettingsData)[] = [
  ...INVESTABLE_ESSENTIAL_FIELDS,
  ...INVESTABLE_ADVANCED_FIELDS,
];

/**
 * Shared logic for Investable Assets form
 *
 * Handles form state, auto-save, and total calculation.
 * Uses useSettingsForm under the hood for Firestore integration.
 */
export function useInvestableAssetsLogic() {
  const { total: totalInvestableAssets, ...rest } =
    useSettingsRHF<InvestableAssetsSettingsData>(
      "investableAssets",
      investableAssetsSchema,
      ALL_INVESTABLE_FIELDS
    );

  return { ...rest, totalInvestableAssets };
}
